import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface BingSnapshotQuery {
  query: string
  clicks: number
  impressions: number
  avgClickPosition: number
  avgImpressionPosition: number
  previousPosition: number | null
  positionChange: number | null
  clicksChange: number | null
  snapshotCount: number
}

export interface BingSnapshotTimeseries {
  snapshot_date: string
  clicks: number
  impressions: number
  avg_click_position: number
  avg_impression_position: number
}

/** List queries with latest metrics + week-over-week changes */
export function useBingSnapshotQueries(siteId: string) {
  return useQuery({
    queryKey: ['bing-snapshots', 'queries', siteId],
    queryFn: () => apiClient.get<{
      queries: BingSnapshotQuery[]
      snapshotDate: string | null
      previousDate: string | null
    }>(`/api/sites/${siteId}/bing/snapshots/queries`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Time-series for a single query */
export function useBingSnapshotTimeseries(siteId: string, query: string) {
  return useQuery({
    queryKey: ['bing-snapshots', 'timeseries', siteId, query],
    queryFn: () => apiClient.get<{
      query: string
      timeseries: BingSnapshotTimeseries[]
    }>(`/api/sites/${siteId}/bing/snapshots/query/${encodeURIComponent(query)}`),
    enabled: !!siteId && !!query,
    staleTime: 5 * 60 * 1000,
  })
}

/** Trigger immediate Bing snapshot */
export function useTakeBingSnapshot(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ queriesStored: number }>(`/api/sites/${siteId}/bing/snapshot`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bing-snapshots', 'queries', siteId] })
    },
  })
}
